/**
 * The application frame: a scrolling body and a tab bar.
 *
 * **Four destinations, and the set is a claim on the thumb strip.** Spec §3 defines a five-tab model and
 * names the five as Home, Pools, a centre capture button, Trips and Profile. The collections tab went
 * with collections: the owner cut the feature, and areas are the single organising axis now, which left
 * Pools as a destination with nothing behind it. What is written down here is the spec's list minus the
 * capture button — ADR-004 puts capture in an overlay — and minus that tab.
 *
 * The choice of *which* destinations is a product decision and is written down rather than implied.
 * **Places, not capture**, because the capture form is a task you finish and not a place you navigate
 * to — a user decides to save a place when they see it, which is why it is reachable from Home's hero
 * field and from a search result rather than from here. It is also the one flow that owns the whole
 * screen: a form with a keyboard open and a tab bar under it loses a row of fields to nothing.
 *
 * That still leaves a slot unclaimed (Explore, which has no public content behind it) and it stays
 * unclaimed. Every tab listed here is a permanent claim on a 390px-wide strip; the rule is not a number
 * but "a destination with something behind it", and four is what that rule produced on the day this
 * changed.
 *
 * **There is one shell, not two.** An earlier version had a second layout for the capture form and the
 * place card — a bare scrolling div with no frame at all, because both are overlays that own the whole
 * screen on a phone. What that also meant, unexamined, is that they had **no left menu on a desktop**:
 * a 1280px window showed a form in a 720px column with 500px of nothing where every other screen puts the
 * rail, and the only way out of the capture form was its own save flow. So the difference between a
 * "shell" screen and an "overlay" one is now one class that hides the tab bar on a phone and nothing
 * else. `ShellLayout` is that choice, and every authenticated route is inside this component.
 *
 * Two structural choices worth stating:
 *
 * - **Grid rows, not `position: fixed`.** On a mobile browser a fixed tab bar interacts badly with the
 *   collapsing address bar and the on-screen keyboard: it ends up floating over content or hidden
 *   under the keyboard. One scrolling row in a `100dvh` grid avoids the whole class of problem, and
 *   `dvh` rather than `vh` because iOS Safari's `100vh` excludes the browser chrome.
 * - **No brand header.** Each screen owns its own header, so it can carry a back control, a title and
 *   its own action. A persistent app-name row above all of them would duplicate that job and spend a
 *   line of a phone screen saying nothing.
 */

import type { ReactElement } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";

import { copy } from "../copy.ts";
import { useCurrentUser, usePublicMode } from "../lib/session-context.tsx";

/**
 * The tabs, in the order a user reaches for them.
 *
 * An array rather than four blocks, so a label, its route and its glyph cannot drift apart — and so
 * the active state is applied the same way to every tab by construction.
 *
 * Places sits second, directly after Home, and that position is deliberate: it is the most-used
 * destination in the product (spec P1 — the saved places are the asset), so it takes the slot a thumb
 * reaches with the least travel. `/places` also matches `/places/:placeId` by prefix, which is
 * deliberate: a place's own card is still the places surface, and the tab stays lit on it.
 *
 * The account is the one entry a deployment can be without. It is listed here with everything else and
 * filtered out below when public mode is on, because the tab is not "hidden" in that deployment — there
 * is no account, and a tab that opens one would be a link to a screen the product does not have.
 */
const TABS = [
  { to: "/home", label: copy.nav.home, icon: "🏠" },
  { to: "/places", label: copy.nav.places, icon: "📍" },
  { to: "/trips", label: copy.nav.trips, icon: "🗺️" },
  { to: "/settings", label: copy.nav.settings, icon: "⚙️" },
] as const;

/**
 * How much chrome the shell shows on a phone.
 *
 * Exported because the route table is what decides it, per route, and a duck-typed string there would be
 * a fourth place for the vocabulary to drift. `shell` is every navigating screen; `overlay` is the two
 * the spec calls tasks — the capture form and the place card — which hide the tab bar on a phone and show
 * the rail on a desktop like everything else.
 */
export type ShellLayout = "shell" | "overlay";

export function AppShell(props: { layout: ShellLayout }): ReactElement {
  /*
   * Read into a boolean before the markup, and that is not tidiness.
   *
   * The stylesheet test mines every quoted string inside a `className={...}` expression — which is how it
   * finds a conditional class, and it is why `place--dragging` is checkable at all. A bare `"overlay"`
   * compared in place of this variable reads to that scan as a class name nobody styles, and the scan is
   * right to complain: it cannot tell a comparison's string from a class list. One line above, and the
   * attribute holds nothing but classes again.
   */
  const hideTabsOnPhone = props.layout === "overlay";
  const user = useCurrentUser();
  /*
   * The account exists only where the deployment has accounts, and that is a decided `private` — not the
   * absence of `public`. This shell only renders once the mode is decided (see `Shell`), so the two agree
   * today; reading it this way means the shell cannot invent an account from a mode nobody answered.
   */
  const hasAccount = usePublicMode() === "private";
  const tabs = TABS;
  /*
   * The account, as the two things it has to say.
   *
   * Initials rather than a picture: nothing in the contract carries an avatar, and the generic silhouette
   * every app falls back to is a row of identical circles that tells nobody anything. A guest has no name
   * yet — the contract says so rather than leaving it empty — and the rail says "Khách" for them, which is
   * the same word the rest of the product uses.
   */
  const accountName = user?.display_name?.trim() ?? "";
  const accountInitials =
    accountName === ""
      ? "?"
      : accountName
          .split(/\s+/)
          .slice(0, 2)
          .map((word) => word.charAt(0).toLocaleUpperCase("vi"))
          .join("");

  return (
    <div className="shell">
      {/*
        `main` and not `div`: a screen-reader user can jump straight to the content, and it is the
        element the tab links are not inside.
      */}
      <main className="shell__main">
        <Outlet />
      </main>

      <nav
        className={hideTabsOnPhone ? "shell__tabs shell__tabs--hidden-on-phone" : "shell__tabs"}
        aria-label="Điều hướng chính"
      >
        {/*
          The brand, for the wide layout only, and it goes home.

          It used to be `aria-hidden` decoration. It is a link now, so it cannot be: an interactive element
          a screen reader cannot reach is worse than no element at all, and the one thing a person expects
          to be able to click is the name of the product. The accessible name is the product's own name from
          the copy layer, which also keeps the two-tone wordmark out of the reading: the visible split into
          "Trip" and "Pool" is a styling decision, and hearing it as one word is the right thing to hear.
          It is display:none below 900px, where the rail is a bottom bar and there is no room for it.
        */}
        <Link className="shell__brand" to="/home" aria-label={copy.app.name}>
          {/*
            A route, drawn: two stops and the leg between them. The product's whole claim is that it puts
            places in a sensible order, and a mark that says that is worth more than the word beside it
            repeated at 18px.
          */}
          <svg className="shell__brand-mark" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="5.5" cy="17.5" r="2.5" />
            <circle cx="18.5" cy="6.5" r="2.5" />
            <path d="M7.4 15.6c2.2-.4 3.1-2.2 4.4-3.9 1.3-1.7 2.6-2.9 4.4-3.5" />
          </svg>
          <span className="shell__brand-name" aria-hidden="true">
            {copy.app.wordmarkLead}
            <span className="shell__brand-accent">{copy.app.wordmarkAccent}</span>
          </span>
        </Link>
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to} className="shell__tab">
            {/*
              `aria-hidden` on the glyph: the label already names the tab, and a screen reader
              announcing "pin" before "Địa điểm" is noise. `NavLink` sets `aria-current="page"`
              itself, which is both the accessible signal and the selector the stylesheet targets — so
              the visible state and the announced state cannot disagree.
            */}
            <span className="shell__tab-icon" aria-hidden="true">
              {tab.icon}
            </span>
            {tab.label}
          </NavLink>
        ))}

        {/*
          Who is signed in, at the foot of the rail.

          The rail is a 232px column of destinations that says nothing about whose destinations they are; the
          profile screen was the only place the account was named, which is one navigation away from every
          question about it. Email under the name because a guest has no name to give and an email is what a
          user recognises their own account by.

          Not rendered where there are no accounts: it is a second link to `/settings`, and it names a reader
          who does not exist in a deployment that serves everyone as one shared account.
        */}
        {hasAccount && (
          <NavLink className="shell__account" to="/settings">
            <span className="shell__account-avatar" aria-hidden="true">
              {accountInitials}
            </span>
            <span className="shell__account-body">
              <span className="shell__account-name">
                {accountName === "" ? copy.nav.settings : accountName}
              </span>
              <span className="shell__account-meta">{user?.email ?? copy.nav.settings}</span>
            </span>
          </NavLink>
        )}
      </nav>
    </div>
  );
}

